import {
  AIRPORTS,
  AirportRef,
  COUNTRY_LABELS,
  REGION_LABELS,
  RegionCode,
} from './airports.data';

/** Scope kinds that can be expanded to a concrete IATA set. */
export type ScopeKind = 'REGION' | 'COUNTRY' | 'AIRPORT' | 'ALL';

export interface Scope {
  kind: ScopeKind;
  /**
   * The code the scope targets:
   * - `REGION`  → a {@link RegionCode} (e.g. `'BR'`)
   * - `COUNTRY` → an ISO 3166-1 alpha-3 country code (e.g. `'BRA'`)
   * - `AIRPORT` → an IATA code (e.g. `'GRU'`)
   * - `ALL`     → ignored (may be omitted)
   */
  code?: string;
}

/** Returns the reference record for an IATA code, or `undefined`. */
export function airport(iata: string): AirportRef | undefined {
  return AIRPORTS[normalize(iata)];
}

/** Returns every airport in the catalog (sorted by IATA). */
export function allAirports(): AirportRef[] {
  return Object.values(AIRPORTS).sort((a, b) => a.iata.localeCompare(b.iata));
}

/** Friendly label for a region code; falls back to the raw code. */
export function regionLabel(code: string): string {
  return REGION_LABELS[code as RegionCode] ?? code;
}

/** Friendly label for an ISO-3 country code; falls back to the raw code. */
export function countryLabel(code: string): string {
  return COUNTRY_LABELS[code] ?? code;
}

/**
 * ISO 3166-1 alpha-3 → alpha-2 for every country represented in the catalog.
 * Consumers that key on alpha-2 (e.g. the `policies` scope resolver's
 * `countryCode`) derive it from an airport via {@link countryIso2}; the spec
 * asserts this table covers every cataloged country, so adding an airport in a
 * new country without extending it fails the build.
 */
const COUNTRY_ISO2: Readonly<Record<string, string>> = Object.freeze({
  ARG: 'AR',
  AUS: 'AU',
  BRA: 'BR',
  CHL: 'CL',
  COL: 'CO',
  ECU: 'EC',
  ESP: 'ES',
  ITA: 'IT',
  NZL: 'NZ',
  PER: 'PE',
  PRT: 'PT',
  PRY: 'PY',
  URY: 'UY',
  USA: 'US',
  // Etihad (EY) network jurisdictions
  ARE: 'AE', QAT: 'QA', BHR: 'BH', KWT: 'KW', OMN: 'OM', SAU: 'SA', JOR: 'JO', LBN: 'LB',
  EGY: 'EG', TUR: 'TR', IND: 'IN', PAK: 'PK', LKA: 'LK', BGD: 'BD', NPL: 'NP', MDV: 'MV',
  GBR: 'GB', IRL: 'IE', FRA: 'FR', DEU: 'DE', NLD: 'NL', GRC: 'GR', CHE: 'CH', THA: 'TH',
  MYS: 'MY', SGP: 'SG', IDN: 'ID', PHL: 'PH', HKG: 'HK', KOR: 'KR', JPN: 'JP', CHN: 'CN',
  CAN: 'CA', ZAF: 'ZA', KEN: 'KE', MAR: 'MA', ETH: 'ET',
  // Air Mauritius (MK) — Mauritius and Madagascar are new jurisdictions; Réunion
  // is FRA and needs no row.
  MUS: 'MU', MDG: 'MG',
  // Southwest (WN) network jurisdictions beyond the US
  MEX: 'MX', JAM: 'JM', BHS: 'BS', CRI: 'CR',
  // Iberia (IB) network jurisdictions
  BEL: 'BE', AUT: 'AT', DNK: 'DK', SWE: 'SE', CZE: 'CZ', POL: 'PL', DZA: 'DZ', ISR: 'IL', PAN: 'PA', SLV: 'SV', GTM: 'GT', CUB: 'CU', DOM: 'DO',
  // Plus Ultra (PU) network — Venezuela is the only jurisdiction the MAD/TFN
  // South-American fan adds that no earlier tenant already reached.
  VEN: 'VE',
});

/**
 * ISO 3166-1 alpha-2 country code for an IATA airport code, or `undefined`
 * when the airport is unknown (or its country is missing from the table).
 */
export function countryIso2(iata: string): string | undefined {
  const ref = AIRPORTS[normalize(iata)];
  return ref ? COUNTRY_ISO2[ref.country] : undefined;
}

/**
 * Friendly label for an airport. By default `"City (IATA)"`; falls back to the
 * raw IATA code when the airport is unknown.
 */
export function airportLabel(iata: string): string {
  const code = normalize(iata);
  const ref = AIRPORTS[code];
  return ref ? `${ref.city} (${ref.iata})` : code;
}

/**
 * Expand a scope into the concrete set of IATA codes it covers.
 *
 * - `{ kind: 'ALL' }`                       → every catalog airport
 * - `{ kind: 'REGION',  code: 'BR' }`       → all airports in that region
 * - `{ kind: 'COUNTRY', code: 'BRA' }`      → all airports in that country
 * - `{ kind: 'AIRPORT', code: 'GRU' }`      → just that airport (if it exists)
 *
 * The result is de-duplicated and sorted. Unknown codes yield an empty set.
 * Region/country matching is case-insensitive; codes are normalized to upper.
 */
export function expandScope(scope: Scope): string[] {
  const { kind } = scope;
  const code = scope.code ? normalize(scope.code) : undefined;

  let matches: AirportRef[];
  switch (kind) {
    case 'ALL':
      matches = Object.values(AIRPORTS);
      break;
    case 'REGION':
      matches = code
        ? Object.values(AIRPORTS).filter((a) => a.region === code)
        : [];
      break;
    case 'COUNTRY':
      matches = code
        ? Object.values(AIRPORTS).filter((a) => a.country === code)
        : [];
      break;
    case 'AIRPORT':
      matches = code && AIRPORTS[code] ? [AIRPORTS[code]] : [];
      break;
    default: {
      // Exhaustiveness guard — a new ScopeKind must be handled above.
      const never: never = kind;
      throw new Error(`Unknown scope kind: ${String(never)}`);
    }
  }

  return [...new Set(matches.map((a) => a.iata))].sort();
}

function normalize(code: string): string {
  return code.trim().toUpperCase();
}
